/**
 * Cloud Sync Service
 * 
 * Handles synchronization between local storage and cloud database
 * - Saves data locally for offline access
 * - Syncs to cloud when online
 * - Loads from cloud and caches locally
 * - Queues changes when offline
 * - Auto-syncs when connection restored
 */
import tokenStore from './tokenStore';
import autoSync from './AutoSync';

class SyncService {
  constructor() {
    this.API_URL = process.env.REACT_APP_BACKEND_URL || '';
    this.userId = null;
    this.deviceId = this.getDeviceId();
    this.syncQueue = [];
    this.isSyncing = false;
    this.isOnline = true; // Default to online
    this.listeners = [];
    
    // Setup online/offline listeners
    this.setupConnectionListeners();
    
    // Check initial online status after a small delay
    setTimeout(() => {
      this.isOnline = navigator.onLine;
    }, 100);
  }

  /**
   * Initialize sync service with user ID.
   * If a team workspace is active (set via setWorkspaceId), all cloud operations
   * will be keyed on the workspace_id instead, so all team members share data.
   */
  initialize(userId) {
    this.userId = userId;
    // Load any previously set workspace from localStorage
    this.workspaceId = localStorage.getItem('maidly_workspace_id') || null;
    console.log('🔄 SyncService initialized — user:', userId, '| workspace:', this.workspaceId || '(none, private mode)');
  }

  /**
   * Set the active team workspace. Pass null/empty to leave the workspace and
   * return to private (per-user) sync.
   */
  setWorkspaceId(workspaceId) {
    if (workspaceId) {
      this.workspaceId = workspaceId;
      localStorage.setItem('maidly_workspace_id', workspaceId);
    } else {
      this.workspaceId = null;
      localStorage.removeItem('maidly_workspace_id');
    }
    console.log('🔄 SyncService workspace updated to:', this.workspaceId || '(private)');
  }

  /**
   * The key used for all cloud sync operations. Workspace wins when set.
   */
  getSyncKey() {
    return this.workspaceId || this.userId;
  }

  /**
   * Get or create device ID
   */
  getDeviceId() {
    let deviceId = localStorage.getItem('maidly_device_id');
    if (!deviceId) {
      deviceId = 'device-' + Math.random().toString(36).substr(2, 9) + '-' + Date.now();
      localStorage.setItem('maidly_device_id', deviceId);
    }
    return deviceId;
  }

  /**
   * Build auth headers (JWT Bearer) for cryptographic enforcement on the
   * backend. Returns an empty object when no token is available, so legacy
   * local-only accounts can still sync solo data (workspace sync requires
   * a real token).
   */
  getAuthHeaders() {
    // Access token lives only in tokenStore (in-memory, Phase 5 hardening)
    const token = tokenStore.getAccessToken();
    return token ? { Authorization: `Bearer ${token}` } : {};
  }

  /**
   * Auth-aware fetch — adds Bearer header, transparently refreshes a 401
   * once via the refresh token (httpOnly cookie), and retries. Inlined to
   * keep this service zero-dependency on the AutoSync write path.
   */
  async _authFetch(url, init = {}) {
    const headers = { ...(init.headers || {}), ...this.getAuthHeaders() };
    let res = await fetch(url, { ...init, headers, credentials: 'include' });
    if (res.status !== 401) return res;

    try {
      const r = await fetch(`${this.API_URL}/api/auth/refresh`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: '{}',
      });
      if (!r.ok) return res;
      const data = await r.json();
      if (!data?.access_token) return res;
      tokenStore.setAccessToken(data.access_token);
      const retryHeaders = {
        ...(init.headers || {}),
        Authorization: `Bearer ${data.access_token}`,
      };
      return await fetch(url, { ...init, headers: retryHeaders, credentials: 'include' });
    } catch {
      return res;
    }
  }

  /**
   * Setup listeners for online/offline events
   */
  setupConnectionListeners() {
    window.addEventListener('online', () => {
      console.log('🌐 Connection restored');
      this.isOnline = true;
      this.notifyListeners('online');
      this.processSyncQueue();
    });

    window.addEventListener('offline', () => {
      console.log('📴 Connection lost');
      this.isOnline = false;
      this.notifyListeners('offline');
    });
  }

  /**
   * Add sync status listener
   */
  addListener(callback) {
    this.listeners.push(callback);
  }

  /**
   * Remove sync status listener
   */
  removeListener(callback) {
    this.listeners = this.listeners.filter(l => l !== callback);
  }

  /**
   * Notify all listeners
   */
  notifyListeners(status, data = {}) {
    this.listeners.forEach(listener => {
      try {
        listener({ status, ...data });
      } catch (error) {
        console.error('Listener error:', error);
      }
    });
  }

  /**
   * Save data (local + cloud)
   */
  /**
   * Sync the app settings OBJECT (company info/logo) to the cloud as a single
   * record so it travels across devices. Does NOT write localStorage here —
   * AutoSync.pushSettingsNow already persisted the stamped object to
   * maidly_settings; this only pushes to the cloud.
   */
  async saveSettings(settingsObj) {
    const syncKey = this.getSyncKey();
    if (!this.isOnline || !syncKey) return { success: false, queued: true };
    try {
      const response = await this._authFetch(`${this.API_URL}/api/sync/save`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...this.getAuthHeaders() },
        body: JSON.stringify({
          user_id: syncKey,
          data_type: 'app_settings',
          data: { ...settingsObj, id: 'app_settings' },
          device_id: this.deviceId,
          requester_user_id: this.userId,
        }),
      });
      const result = await response.json();
      return { success: !!result.success };
    } catch (error) {
      console.error('Settings sync error:', error);
      return { success: false, error: error.message };
    }
  }

  async save(dataType, data) {
    try {
      // 1. Save locally immediately (fast)
      this.saveLocal(dataType, data);
      
      // 2. Sync to cloud (background) — uses workspace key when in a team
      const syncKey = this.getSyncKey();
      if (this.isOnline && syncKey) {
        this.notifyListeners('syncing', { dataType });
        
        const response = await this._authFetch(`${this.API_URL}/api/sync/save`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...this.getAuthHeaders()
          },
          body: JSON.stringify({
            user_id: syncKey,
            data_type: dataType,
            data: data,
            device_id: this.deviceId,
            requester_user_id: this.userId
          })
        });

        const result = await response.json();
        
        if (result.success) {
          console.log(`✅ Synced ${dataType} to cloud`);
          this.notifyListeners('synced', { dataType });
          return { success: true, synced: true };
        } else {
          throw new Error(result.error || 'Sync failed');
        }
      } else if (!this.isOnline) {
        // Add to queue for later sync
        this.addToQueue('save', dataType, data);
        this.notifyListeners('queued', { dataType });
        return { success: true, synced: false, queued: true };
      }
      
      return { success: true, synced: false };
    } catch (error) {
      console.error(`Sync error for ${dataType}:`, error);
      // Still saved locally, will sync later
      this.addToQueue('save', dataType, data);
      this.notifyListeners('error', { dataType, error: error.message });
      return { success: true, synced: false, error: error.message };
    }
  }

  /**
   * Load data (cloud or local)
   */
  async load(dataType) {
    try {
      // Try to load from cloud if online
      const syncKey = this.getSyncKey();
      if (this.isOnline && syncKey) {
        console.log(`🔄 Loading ${dataType} from cloud...`);
        this.notifyListeners('loading', { dataType });
        
        const response = await this._authFetch(
          `${this.API_URL}/api/sync/load/${syncKey}/${dataType}?requester_user_id=${encodeURIComponent(this.userId || '')}`,
          { headers: { ...this.getAuthHeaders() } }
        );
        
        const result = await response.json();
        
        if (result.success && result.data) {
          // Cache locally
          this.saveLocal(dataType, result.data);
          console.log(`✅ Loaded ${result.count || result.data.length} ${dataType} from cloud`);
          this.notifyListeners('loaded', { dataType, count: result.count });
          return { success: true, data: result.data };
        }
      }
      
      // Fallback to local
      console.log(`📦 Loading ${dataType} from local storage`);
      const localData = this.loadLocal(dataType);
      return { success: true, data: localData, fromLocal: true };
      
    } catch (error) {
      console.error(`Load error for ${dataType}:`, error);
      // Fallback to local
      const localData = this.loadLocal(dataType);
      return { success: true, data: localData, fromLocal: true, error: error.message };
    }
  }

  /**
   * Bulk save all data types
   */
  async bulkSave(allData) {
    try {
      // 1. Save all locally
      Object.keys(allData).forEach(dataType => {
        this.saveLocal(dataType, allData[dataType]);
      });
      
      // 2. Sync to cloud — uses workspace key when in a team
      const syncKey = this.getSyncKey();
      if (this.isOnline && syncKey) {
        this.notifyListeners('syncing', { bulk: true });
        
        const response = await this._authFetch(`${this.API_URL}/api/sync/bulk-save`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...this.getAuthHeaders()
          },
          body: JSON.stringify({
            user_id: syncKey,
            device_id: this.deviceId,
            data: allData,
            requester_user_id: this.userId
          })
        });

        const result = await response.json();
        
        if (result.success) {
          console.log('✅ Bulk sync complete:', result.results);
          this.notifyListeners('synced', { bulk: true, results: result.results });
          return { success: true, synced: true, results: result.results };
        }
      }
      
      return { success: true, synced: false };
    } catch (error) {
      // "Failed to fetch" / AbortError / TypeError happen any time the network
      // blips, a tab goes to sleep, or the local backend hot-reloads. None of
      // these need a scary red console.error — the offline queue retries on
      // its own. Surface real HTTP/parse errors at error level, network blips
      // at debug level so the console stays clean.
      const isNetworkBlip = error?.name === 'TypeError'
        || error?.name === 'AbortError'
        || /Failed to fetch|NetworkError|Load failed/i.test(error?.message || '');
      if (isNetworkBlip) {
        console.debug('[sync] bulkSave offline — queued for retry');
      } else {
        console.error('Bulk sync error:', error);
      }
      this.addToQueue('bulkSave', 'all', allData);
      return { success: true, synced: false, error: error.message };
    }
  }

  /**
   * Bulk load all data types
   */
  async bulkLoad() {
    try {
      const syncKey = this.getSyncKey();
      if (this.isOnline && syncKey) {
        console.log('🔄 Loading all data from cloud...');
        this.notifyListeners('loading', { bulk: true });
        
        const response = await this._authFetch(
          `${this.API_URL}/api/sync/bulk-load/${syncKey}?requester_user_id=${encodeURIComponent(this.userId || '')}`,
          { headers: { ...this.getAuthHeaders() } }
        );
        
        const result = await response.json();
        
        if (result.success && result.data) {
          // Cache all locally
          Object.keys(result.data).forEach(dataType => {
            this.saveLocal(dataType, result.data[dataType]);
          });
          
          console.log('✅ Bulk load complete. Types:', result.types);
          this.notifyListeners('loaded', { bulk: true, types: result.types });
          return result.data;
        }
      }
      
      // Fallback: load all from local
      return this.loadAllLocal();
      
    } catch (error) {
      const isNetworkBlip = error?.name === 'TypeError'
        || error?.name === 'AbortError'
        || /Failed to fetch|NetworkError|Load failed/i.test(error?.message || '');
      if (isNetworkBlip) {
        console.debug('[sync] bulkLoad offline — using local cache');
      } else {
        console.error('Bulk load error:', error);
      }
      return this.loadAllLocal();
    }
  }

  /**
   * Delete data
   */
  async delete(dataType, itemId) {
    try {
      // Remove from local
      const localData = this.loadLocal(dataType);
      const filtered = localData.filter(item => item.id !== itemId);
      this.saveLocal(dataType, filtered);
      
      // Delete from cloud
      const syncKey = this.getSyncKey();
      if (this.isOnline && syncKey) {
        await this._authFetch(
          `${this.API_URL}/api/sync/delete/${syncKey}/${dataType}/${itemId}?requester_user_id=${encodeURIComponent(this.userId || '')}`,
          { method: 'DELETE', headers: { ...this.getAuthHeaders() } }
        );
        console.log(`✅ Deleted ${dataType} ${itemId} from cloud`);
      } else {
        this.addToQueue('delete', dataType, { id: itemId });
      }
      
      return { success: true };
    } catch (error) {
      console.error('Delete error:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Save to local storage
   */
  saveLocal(dataType, data) {
    try {
      // Route through AutoSync's suppressed writer so caching already-synced
      // data (after push/load/delete) does NOT re-enter the push pipeline.
      // Previously this used the wrapped localStorage.setItem, which created a
      // push -> saveLocal -> push feedback loop (dozens of redundant
      // bulk-saves) that could hit rate limits or race a stale echo over a
      // fresh write — the root cause of "data don't save".
      autoSync.writeCache(dataType, data);
    } catch (error) {
      console.error('Local save error:', error);
    }
  }

  /**
   * Load from local storage
   */
  loadLocal(dataType) {
    try {
      const key = `maidly_${dataType}`;
      const data = localStorage.getItem(key);
      return data ? JSON.parse(data) : [];
    } catch (error) {
      console.error('Local load error:', error);
      return [];
    }
  }

  /**
   * Load all from local storage
   */
  loadAllLocal() {
    const dataTypes = ['invoices', 'customers', 'quotes', 'inventory', 'employees', 'appointments'];
    const allData = {};
    
    dataTypes.forEach(type => {
      allData[type] = this.loadLocal(type);
    });
    
    return allData;
  }

  /**
   * Add to sync queue
   */
  addToQueue(action, dataType, data) {
    this.syncQueue.push({
      action,
      dataType,
      data,
      timestamp: Date.now()
    });
    
    // Save queue to localStorage
    localStorage.setItem('maidly_sync_queue', JSON.stringify(this.syncQueue));
    
    console.log(`📝 Queued: ${action} ${dataType} (${this.syncQueue.length} items in queue)`);
  }

  /**
   * Process sync queue
   */
  async processSyncQueue() {
    if (this.isSyncing || !this.isOnline || this.syncQueue.length === 0) {
      return;
    }

    this.isSyncing = true;
    this.notifyListeners('syncing', { queue: true, count: this.syncQueue.length });
    
    console.log(`🔄 Processing sync queue (${this.syncQueue.length} items)...`);

    const queue = [...this.syncQueue];
    this.syncQueue = [];

    for (const item of queue) {
      try {
        if (item.action === 'save') {
          await this.save(item.dataType, item.data);
        } else if (item.action === 'delete') {
          await this.delete(item.dataType, item.data.id);
        } else if (item.action === 'bulkSave') {
          await this.bulkSave(item.data);
        }
      } catch (error) {
        console.error('Queue processing error:', error);
        // Re-add to queue
        this.syncQueue.push(item);
      }
    }

    // Save updated queue
    localStorage.setItem('maidly_sync_queue', JSON.stringify(this.syncQueue));

    this.isSyncing = false;
    
    if (this.syncQueue.length === 0) {
      console.log('✅ Sync queue processed');
      this.notifyListeners('synced', { queue: true });
    }
  }

  /**
   * Load queue from storage
   */
  loadQueue() {
    try {
      const queueData = localStorage.getItem('maidly_sync_queue');
      if (queueData) {
        this.syncQueue = JSON.parse(queueData);
        console.log(`📝 Loaded ${this.syncQueue.length} items from sync queue`);
      }
    } catch (error) {
      console.error('Queue load error:', error);
    }
  }

  /**
   * Clear sync queue
   */
  clearQueue() {
    this.syncQueue = [];
    localStorage.removeItem('maidly_sync_queue');
    console.log('🗑️ Sync queue cleared');
  }

  /**
   * Get sync status
   */
  async getSyncStatus() {
    try {
      const syncKey = this.getSyncKey();
      if (this.isOnline && syncKey) {
        const response = await this._authFetch(
          `${this.API_URL}/api/sync/status/${syncKey}`,
          { headers: { ...this.getAuthHeaders() } }
        );
        
        const result = await response.json();
        
        return {
          online: this.isOnline,
          queued: this.syncQueue.length,
          cloudStats: result.stats || [],
          totalItems: result.total_items || 0
        };
      }
      
      return {
        online: false,
        queued: this.syncQueue.length,
        cloudStats: [],
        totalItems: 0
      };
    } catch (error) {
      console.error('Status check error:', error);
      return {
        online: this.isOnline,
        queued: this.syncQueue.length,
        error: error.message
      };
    }
  }
}

// Create singleton instance
const syncService = new SyncService();

export default syncService;
